import { makeAutoObservable } from "mobx";
import { centerClient } from "../../../shared/clients";
import type { Client as ClientDto, Account as AccountDto, ModelInfo, Account, Device as IDevice, Timestamp } from "../../../shared/generated";
import { TreeNode } from "rsuite/esm/internals/Tree/types";
import { toast } from "react-toastify";
import { ConfirmDialogProps } from "../../../utils/ConfirmDialog";
import { confirm } from "../../../utils/confirm";
export const localStorageKeys = {
    currentNode: "CurrentNode",
    clientsTreeWidth: "ClientsTreeWidth",
    clientsTableWidths: "ClientsTableWidths",
    accountsTableWidths: "AccountsTableWidths",
    autoRefreshEnabled: "AutoRefreshEnabled",
    autoRefreshIntervalSec: "AutoRefreshIntervalSec"
}

export class TimeStamp {
    Nanos: number = 0;
    Seconds: number = 0;
    constructor(data?) {
        this.Seconds = data?.Seconds ?? 0;
        this.Nanos = data?.Nanos ?? 0;
    }
    toDateString = () => {
        if (!this.Seconds)
            return '';
        return new Date(this.Seconds * 1000 + this.Nanos / 1000000).toLocaleString();
    }
}

// сырые значения формы: ключи — поля Device, значения приходят из инпутов
export type DeviceValues = Partial<Record<keyof IDevice, string | number | boolean | undefined>>;

const toNumber = (value: unknown, fallback = 0): number => {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
};

const toStr = (value: unknown): string | undefined =>
    value == null ? undefined : String(value);

export class Device implements IDevice {
    Balance: number = 0;
    Banned: boolean = false;
    Blocked: boolean = false;
    Charge: number = 0;
    Comment: string | undefined;
    Cost: number = 0;
    DeviceModelName: string | undefined;
    Id: string | undefined;
    Imei: string | undefined;
    LastDataTime: Timestamp;
    Model: number = 0;
    SaveTime: number = 0;
    Serial: string | undefined;
    TodayCount: number = 0;
    StoragePeriod: number = 14;
    Logins: string[] | undefined = [];
    TestMode: string | undefined;
    JsonProps: string | undefined;
    Company: string | undefined;
    constructor(data: DeviceValues) {
        this.Balance = toNumber(data.Balance);
        this.Banned = !!data.Banned;
        this.Blocked = !!data.Blocked;
        this.Charge = toNumber(data.Charge);
        this.Comment = toStr(data.Comment);
        this.Cost = toNumber(data.Cost);
        this.DeviceModelName = toStr(data.DeviceModelName);
        this.Id = toStr(data.Id);
        this.Imei = toStr(data.Imei);
        this.LastDataTime = new TimeStamp(data.LastDataTime);
        this.Model = toNumber(data.Model);
        this.SaveTime = toNumber(data.SaveTime);
        this.Serial = toStr(data.Serial);
        this.TodayCount = toNumber(data.TodayCount);
        this.StoragePeriod = toNumber(data.StoragePeriod, 14);
        this.Logins = (data.Logins as unknown as string[] | undefined) ?? [];
        this.TestMode = toStr(data.TestMode);
        this.JsonProps = toStr(data.JsonProps);
        this.Company = toStr(data.Company);
    }
}
interface IFoundDeviceToShow {
    deviceSerial: string; 
    deviceModel: number;
}

export interface IClientsTreeNode extends TreeNode {
    id: string,
    type: string,
    name: string,
    parentId: string,
    key: string,
    parentNodeId?: string,
    index?: number
}
export interface IDeviceModel {
    ModelId: number;
    ModelName: string;
}
export type { ModelInfo };
export const darkTheme = "dark";
export const lightTheme = "light";
const lsName = 'center-theme';
const defaultTheme = darkTheme;
export class Context {
    clientsTreeData: IClientsTreeNode[];
    setClientsTreeData = (value) => {
        this.clientsTreeData = value;
        this.lastRefreshDate = new Date().toLocaleString("ru-RU");
    }
    isClientsTreeDataLoading: boolean = false;
    setIsClientsTreeDataLoading = (value: boolean) => {
        this.isClientsTreeDataLoading = value;
    }
    lastRefreshDate: string = "";
    refreshTreeCounter: number = 0;
    refreshTree = () => {
        this.refreshTreeCounter += 1;
        this.showRightClickPopup = false;
    }
    autoRefreshEnabled: boolean = false;
    autoRefreshIntervalSec: number = 30;
    setAutoRefreshEnabled = (value: boolean) => {
        this.autoRefreshEnabled = value;
        localStorage.setItem(localStorageKeys.autoRefreshEnabled, JSON.stringify(value));
    }
    setAutoRefreshIntervalSec = (value: number) => {
        this.autoRefreshIntervalSec = value;
        localStorage.setItem(localStorageKeys.autoRefreshIntervalSec, JSON.stringify(value));
    }
    foundDeviceToShow: IFoundDeviceToShow | null = null;
    setFoundDeviceToShow = (value) => {
        this.foundDeviceToShow = value;
    }
    currentNode: IClientsTreeNode | null = null;
    setCurrentNode = (value: IClientsTreeNode | null) => {
        if (this.currentNode == value)
            return;

        this.currentNode = value;
        localStorage.setItem(localStorageKeys.currentNode, JSON.stringify(value));
    }
    pendingScrollNodeKey: string | null = null;
    setPendingScrollNodeKey = (value: string | null) => {
        this.pendingScrollNodeKey = value;
    }
    clientAccounts: Account[] = [];
    clientAccountsClientId: string | null = null;
    setClientAccounts = (clientId: string | null, accounts: Account[]) => {
        this.clientAccountsClientId = clientId;
        this.clientAccounts = accounts;
    }
    showClientSettings: boolean = false;
    setShowClientSettings = (value: boolean) => {
        this.showClientSettings = value;
    }
    showAccountSettings: boolean = false;
    setShowAccountSettings = (value: boolean) => {
        this.showAccountSettings = value;
    }
    theme: string = defaultTheme;
    setTheme = (value: string) => {
        this.theme = value;
    }
    constructor() {
        makeAutoObservable(this);
        const lsNode = localStorage.getItem(localStorageKeys.currentNode);
        if (lsNode) {
            try {
                this.currentNode = JSON.parse(lsNode) as IClientsTreeNode;
            } catch {
                this.currentNode = null;
            }
        }
        const lsTheme = localStorage.getItem(lsName);
        if (lsTheme) {       
            this.theme = JSON.parse(lsTheme);       
        }
        const lsAutoRefreshEnabled = localStorage.getItem(localStorageKeys.autoRefreshEnabled);
        if (lsAutoRefreshEnabled != null) {
            try {
                this.autoRefreshEnabled = JSON.parse(lsAutoRefreshEnabled) === true;
            } catch {
                this.autoRefreshEnabled = false;
            }
        }
        const lsAutoRefreshIntervalSec = localStorage.getItem(localStorageKeys.autoRefreshIntervalSec);
        if (lsAutoRefreshIntervalSec != null) {
            const n = Number(JSON.parse(lsAutoRefreshIntervalSec));
            if (Number.isFinite(n) && n > 0)
                this.autoRefreshIntervalSec = n;
        }
    }
    successRemoveClient = () => {
        toast.success("Клиент успешно удален");
        this.setCurrentNode(null);
        this.refreshTree();
    }
    removeClient = () => {
        if (this.currentNode == null)
            return false;
        const client: ClientDto = {
            Id: this.currentNode.id,
            Name: this.currentNode.name
        } as ClientDto;
        centerClient.removeClient(client)
            .then(() => this.successRemoveClient())
            .catch(() => void 0);
    }
    handleDeleteClient = async () => {
        const removeClientConfirm = confirm(new ConfirmDialogProps('Удалить клиента ' + this.currentNode?.name + '?'));
        if (await removeClientConfirm) {
            this.removeClient();
        }
    };
    removeAccount = () => {
        const account: AccountDto = {
            Login: this.currentNode?.name,
            ClientId: this.currentNode?.parentId
        } as AccountDto;
        centerClient.removeAccount(account)
            .then(() => {
                toast.success("Логин успешно удален");
                this.refreshTree();
            })
            .catch(() => void 0);
    }
    handleDeleteAccount = async () => {
        const removeAccountConfirm = confirm(new ConfirmDialogProps('Удалить логин ' + this.currentNode?.name + '?'));
        if (await removeAccountConfirm) {
            this.removeAccount();
        }
    };
    findDeviceId: string;
    setFindDeviceId = (value) => {
        this.findDeviceId = value;
    }
    foundDevices: ClientsTreeSearchResult[] = [];
    setFoundDevices = (value) => {
        this.foundDevices = value;
    }
    showMultipleDevicesFound: boolean;
    setShowMultipleDevicesFound = (value) => {
        this.showMultipleDevicesFound = value;
    }
    newClientId: string | null;
    setNewClientId = (value: string | null) => {
        this.newClientId = value;
        this.setCurrentNode(null);
    }
    newAccount: Account | null = null;
    setNewAccount = (value: Account | null) => {
        this.newAccount = value;
        this.setCurrentNode(null);
    }
    deviceModels: ModelInfo[] = [];
    setDeviceModels = (value: ModelInfo[]) => {
        this.deviceModels = value;
    }
    loadDeviceModels = () => {
        centerClient.getDeviceModels()
            .then((res) => {
                const items = res.data?.Items;
                if (items)
                    this.setDeviceModels(items);
            })
            .catch(() => void 0);
    }
    tabIndex: number = 0;
    setTabIndex = (value: number) => {
        this.tabIndex = value;
    }
    showRightClickPopup: boolean;
    setShowRightClickPopup = (value: boolean) => {
        this.showRightClickPopup = value; 
    }
}
const context = new Context();
export default context;